import { useState } from 'react';
import {
  ORIGIN_LABEL,
  roundPrice,
  STEEL_BAR_LENGTH_M,
  typedPriceField,
  WARNING_LABEL,
  type MaterialPrice,
  type ProductUnitFactor,
  type ResolvedMaterialPrice,
} from '@obraplan/core/domain/takeoff/material-price';
import {
  steelGaugeInfo,
  steelGaugeLabel,
  type SteelGauge,
} from '@obraplan/core/domain/takeoff/steel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/cn';
import { NumeroOpcionalInput } from './campos';
import { bitolaDo, fatorSugeridoDaBarra, kgPorBarra } from './completar-para-orcar';
import { CorrecaoComMotivo } from './CorrecaoComMotivo';
import { precoUnitario } from './formato';
import { compararUnidades } from './preco-do-compras';
import {
  linhasDaFoto,
  ROTULO_CURTO_DA_ORIGEM,
  textoDoFator,
  TOM_DA_ORIGEM,
  type DecisaoDeFator,
  type DecisaoDePreco,
} from './precos-da-obra';

/**
 * O preço de um material na obra — a célula que o mostra e o diálogo que o decide (Fase 29,
 * etapa 2 — RN-397, RN-398).
 *
 * A célula serve às duas tabelas da consolidação (por material e por disciplina); o diálogo
 * só abre para quem edita o quantitativo (D13). Quem só vê lê o mesmo preço, a mesma origem e
 * o mesmo aviso — e não tem botão nenhum.
 */

/** O material como o preço o enxerga: a chave da consolidação (D7), o código e a unidade. */
export interface MaterialDoPreco {
  readonly key: string;
  readonly code?: string;
  readonly description: string;
  readonly unit?: string;
  /** A bitola, quando o catálogo a sabe; sem ela, a descrição a diz ("CA 50 - 16 MM"). */
  readonly steelGauge?: SteelGauge;
}

/** De onde veio o fator que entrou na conta — dito ao lado do número, para ninguém o confundir. */
function origemDoFator(resolved: ResolvedMaterialPrice): string {
  if (resolved.factorSource === 'OBRA') return 'fator da obra';
  return resolved.factorBasis === 'BITOLA'
    ? `fator do produto · barra de ${String(STEEL_BAR_LENGTH_M)} m`
    : 'fator do produto';
}

/**
 * O preço que vale, com a origem e o aviso — ou "—", com o porquê.
 *
 * O selo diz de onde veio o número (médio, provisório, digitado), e nunca só por cor (§4.4):
 * provisório no lugar do médio é o orçamento dizendo que ainda não confia nele. O aviso é o
 * de `WARNING_LABEL`, palavra por palavra — é a mesma frase em todo lugar que fala do preço.
 * O fator aparece quando entrou na conta, para o número não parecer o do compras — e de onde
 * ele veio: o do produto (todas as obras) ou o desta obra, que vence o do produto.
 */
export function PrecoNaCelula({
  material,
  price,
  resolved,
  podeEditar,
  definir,
  fatorDoProduto,
  definirFator,
}: {
  material: MaterialDoPreco;
  price: MaterialPrice | undefined;
  resolved: ResolvedMaterialPrice;
  podeEditar: boolean;
  definir: (key: string, decisao: DecisaoDePreco, code?: string) => Promise<boolean>;
  /** O fator que o produto já tem — mostrado no diálogo mesmo quando o da obra vence. */
  fatorDoProduto?: ProductUnitFactor | undefined;
  /** Grava o fator do produto, para todas as obras (decidido em 13/10/2026). */
  definirFator: (fator: DecisaoDeFator) => Promise<boolean>;
}) {
  const [aberto, setAberto] = useState(false);
  const conteudo = (
    <span className="flex flex-col items-end gap-0.5">
      <span className="flex flex-wrap items-center justify-end gap-1.5">
        <span className={cn('tabular', resolved.unitPrice !== undefined && 'font-medium')}>
          {resolved.unitPrice === undefined ? '—' : precoUnitario(resolved.unitPrice)}
        </span>
        {resolved.origin !== undefined && (
          <Badge
            tone={TOM_DA_ORIGEM[resolved.origin]}
            title={ORIGIN_LABEL[resolved.origin]}
            className="px-1.5 py-0 text-[11px]"
          >
            {ROTULO_CURTO_DA_ORIGEM[resolved.origin]}
          </Badge>
        )}
      </span>
      {resolved.factorApplied !== undefined && price?.supply?.average !== undefined && (
        <span className="tabular text-[11px] text-(--color-content-muted)">
          {`${precoUnitario(price.supply.average)}${
            price.supply.unit === undefined ? '' : `/${price.supply.unit}`
          } × ${textoDoFator(resolved.factorApplied)} · ${origemDoFator(resolved)}`}
        </span>
      )}
      {resolved.warning !== undefined && (
        <span className="text-[11px] text-(--color-state-warn)">
          {WARNING_LABEL[resolved.warning]}
        </span>
      )}
    </span>
  );

  if (!podeEditar) return conteudo;
  return (
    <>
      <button
        type="button"
        aria-label={`Preço de ${material.description}`}
        className="-mx-1 rounded-md px-1 py-0.5 text-right hover:bg-(--color-surface-muted) focus-visible:outline-2 focus-visible:outline-(--color-brand)"
        onClick={() => setAberto(true)}
      >
        {conteudo}
      </button>
      <Dialog open={aberto} onOpenChange={setAberto}>
        <DialogContent>
          {/* Montado só aberto: reabrir começa do que está gravado, e não do que se digitou. */}
          {aberto && (
            <DecidirPreco
              material={material}
              price={price}
              resolved={resolved}
              definir={definir}
              fatorDoProduto={fatorDoProduto}
              definirFator={definirFator}
              onFechar={() => setAberto(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * O diálogo do preço: a foto, a unidade, o fator e o número digitado.
 *
 * ## Onde cai o número digitado (D8)
 *
 * `typedPriceField` decide, e a tela só obedece: por cima de um médio que vale, é o
 * **digitado**, e pede motivo — com o mesmo componente e a mesma pergunta da correção da
 * quantidade (`CorrecaoComMotivo`), porque é o mesmo gesto: discordar de um número que veio de
 * outro lugar. Onde não há médio que valha, é o **provisório**, sem motivo: não há número do
 * compras sendo contrariado.
 *
 * ## O fator (D6)
 *
 * Só aparece quando as unidades diferem, e escrito como a frase que ele é: "1 M = [ ] UN".
 * Sem ele, o médio do compras não vale — o preço do quilo no lugar do da barra erraria o
 * orçamento sem que nenhum número parecesse estranho.
 *
 * Grava no **produto** por padrão (decidido em 13/10/2026): "1 L = 1/18 UN" é verdade para o
 * desmoldante em qualquer obra, e digitá-lo de novo em cada uma era o trabalho repetido que a
 * decisão tirou. "Só nesta obra" grava o da obra, que vence o do produto aqui — a obra que
 * comprou a barra de outro comprimento. O vergalhão sem fator nenhum chega com a barra de 12 m
 * da bitola **no campo**, com a conta à vista, e só vale depois de alguém gravar: a sugestão
 * deixou de entrar sozinha no preço.
 *
 * Cada gravação vai na hora para o servidor (fora do rascunho da bancada), e o diálogo fica
 * aberto mostrando o resultado: quem acabou de pôr o fator quer ver o preço que ele deu.
 */
function DecidirPreco({
  material,
  price,
  resolved,
  definir,
  fatorDoProduto,
  definirFator,
  onFechar,
}: {
  material: MaterialDoPreco;
  price: MaterialPrice | undefined;
  resolved: ResolvedMaterialPrice;
  definir: (key: string, decisao: DecisaoDePreco, code?: string) => Promise<boolean>;
  fatorDoProduto: ProductUnitFactor | undefined;
  definirFator: (fator: DecisaoDeFator) => Promise<boolean>;
  onFechar: () => void;
}) {
  const foto = price?.supply;
  const unidadeDoItem = material.unit?.trim() === '' ? undefined : material.unit?.trim();
  const unidadeDoCompras = foto?.unit?.trim() === '' ? undefined : foto?.unit?.trim();
  const unidade = compararUnidades(unidadeDoCompras, unidadeDoItem);
  const campo = typedPriceField(resolved);
  const porUnidade = unidadeDoItem === undefined ? '' : `/${unidadeDoItem}`;
  const bitola = bitolaDo(material);
  /** A barra de 12 m da bitola — só sugestão: entra no campo, e não no preço. */
  const sugestao =
    material.code === undefined || bitola === undefined
      ? undefined
      : fatorSugeridoDaBarra(
          { ...(unidadeDoItem === undefined ? {} : { unit: unidadeDoItem }), steelGauge: bitola },
          unidadeDoCompras,
        );
  const semFator = price?.unitFactor === undefined && fatorDoProduto === undefined;

  const [gravando, setGravando] = useState(false);
  const [digitando, setDigitando] = useState(false);
  const [fator, setFator] = useState<number | undefined>(
    price?.unitFactor ?? fatorDoProduto?.factor ?? sugestao,
  );
  const [provisorio, setProvisorio] = useState<number | undefined>(price?.provisionalPrice);

  const gravar = async (decisao: DecisaoDePreco): Promise<boolean> => {
    setGravando(true);
    try {
      return await definir(material.key, decisao, material.code);
    } finally {
      setGravando(false);
    }
  };

  /**
   * O fator do produto, para todas as obras — esta inclusive: o fator só desta obra, se havia,
   * sai, ou o número gravado não mudaria o preço que está à vista. Vazio tira o do produto.
   */
  const gravarNoProduto = async (): Promise<void> => {
    if (material.code === undefined || unidadeDoItem === undefined) return;
    setGravando(true);
    try {
      const feito = await definirFator({
        code: material.code,
        itemUnit: unidadeDoItem,
        ...(unidadeDoCompras === undefined ? {} : { supplyUnit: unidadeDoCompras }),
        factor: fator ?? null,
        // A sugestão confirmada é a barra da bitola; qualquer outro número foi digitado.
        basis: fator !== undefined && fator === sugestao ? 'BITOLA' : 'DIGITADO',
      });
      if (feito && fator !== undefined && price?.unitFactor !== undefined) {
        await definir(material.key, { unitFactor: null }, material.code);
      }
    } finally {
      setGravando(false);
    }
  };
  const fatorValido = fator === undefined || fator > 0;
  const nadaMudaNoProduto =
    fator === undefined
      ? fatorDoProduto === undefined
      : fator === fatorDoProduto?.factor && price?.unitFactor === undefined;

  return (
    <>
      <DialogHeader>
        <DialogTitle>{`Preço de ${material.description}`}</DialogTitle>
        <DialogDescription>
          {`${material.code ?? 'sem código de compras'} · ${
            unidadeDoItem === undefined ? 'item sem unidade' : `preço por ${unidadeDoItem}`
          }`}
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-4">
        {/* O que vale agora — o mesmo que a tabela mostra, com a origem por extenso. */}
        <div className="flex flex-col gap-0.5 rounded-md border border-(--color-border-subtle) px-3 py-2">
          <p className="flex flex-wrap items-center gap-2 text-sm">
            <span className="tabular font-semibold" data-preco-que-vale>
              {resolved.unitPrice === undefined
                ? 'Sem preço'
                : `${precoUnitario(resolved.unitPrice)}${porUnidade}`}
            </span>
            {resolved.origin !== undefined && (
              <Badge tone={TOM_DA_ORIGEM[resolved.origin]}>{ORIGIN_LABEL[resolved.origin]}</Badge>
            )}
          </p>
          {resolved.warning !== undefined && (
            <p className="text-xs text-(--color-state-warn)">{WARNING_LABEL[resolved.warning]}</p>
          )}
          {price?.overrideReason !== undefined && (
            <p className="text-xs text-(--color-content-muted)">
              {`Motivo do digitado: ${price.overrideReason}`}
            </p>
          )}
        </div>

        {/* A foto do compras (D5), inteira: o número e de onde ele saiu. */}
        <section aria-label="Foto do compras" className="flex flex-col gap-0.5 text-xs">
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-(--color-content-muted)">
            Foto do compras
          </h3>
          {material.code === undefined ? (
            <p className="text-(--color-content-muted)">
              Sem código de compras: não há o que perguntar ao compras, e o preço deste material é o
              provisório.
            </p>
          ) : foto === undefined ? (
            <p className="text-(--color-content-muted)">
              Ainda sem foto do compras — use “Atualizar preços” na consolidação.
            </p>
          ) : (
            linhasDaFoto(foto).map((linha) => (
              <p key={linha} className="tabular">
                {linha}
              </p>
            ))
          )}
        </section>

        {unidade !== undefined && (
          <p
            className={cn(
              'text-xs',
              unidade.diferente ? 'text-(--color-state-warn)' : 'text-(--color-content-muted)',
            )}
          >
            {unidade.texto}
          </p>
        )}

        {unidade?.diferente === true && unidadeDoItem !== undefined && (
          <section aria-label="Fator de unidade" className="flex flex-col gap-2">
            {fatorDoProduto !== undefined && (
              <p className="text-xs text-(--color-content-muted)">
                {`Fator do produto, para todas as obras: 1 ${unidadeDoItem} = ${textoDoFator(
                  fatorDoProduto.factor,
                )} ${fatorDoProduto.supplyUnit ?? unidadeDoCompras ?? ''}${
                  fatorDoProduto.basis === 'BITOLA'
                    ? ` — a barra de ${String(STEEL_BAR_LENGTH_M)} m da bitola, conferida`
                    : ''
                }.`}
              </p>
            )}
            {price?.unitFactor !== undefined && (
              <p className="text-xs text-(--color-content-muted)">
                {`Só nesta obra: 1 ${unidadeDoItem} = ${textoDoFator(price.unitFactor)} ${
                  unidadeDoCompras ?? ''
                } — vale aqui no lugar do fator do produto.`}
              </p>
            )}
            {semFator && sugestao !== undefined && bitola !== undefined && (
              // A conta à vista, e nada gravado: quem confere é gente (13/10/2026).
              <p className="text-xs text-(--color-state-warn)">
                {`Sugestão pela bitola, ainda não confirmada: a barra de ${String(
                  STEEL_BAR_LENGTH_M,
                )} m de ${steelGaugeLabel(bitola)} pesa ${steelGaugeInfo(
                  bitola,
                ).kgPerMeter.toLocaleString(
                  'pt-BR',
                )} kg/m × ${String(STEEL_BAR_LENGTH_M)} m = ${kgPorBarra(bitola).toLocaleString(
                  'pt-BR',
                  {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  },
                )} kg, então 1 ${unidadeDoItem} = ${textoDoFator(sugestao)} ${
                  unidadeDoCompras ?? ''
                }. Confira e grave — barra de outro comprimento, digite o fator dela.`}
              </p>
            )}
            <form
              className="flex flex-wrap items-center gap-2 text-sm"
              onSubmit={(event) => {
                event.preventDefault();
                if (nadaMudaNoProduto || !fatorValido) return;
                void gravarNoProduto();
              }}
            >
              <span className="tabular">{`1 ${unidadeDoItem} =`}</span>
              <NumeroOpcionalInput
                aria-label={`Fator: quantos ${unidadeDoCompras ?? ''} em 1 ${unidadeDoItem}`}
                placeholder="fator"
                className="h-9 w-28 text-right"
                value={fator}
                onChange={setFator}
              />
              <span>{unidadeDoCompras}</span>
              <Button
                type="submit"
                size="sm"
                variant="secondary"
                disabled={gravando || !fatorValido || nadaMudaNoProduto}
              >
                {fator === undefined && fatorDoProduto !== undefined
                  ? 'Tirar o fator do produto'
                  : 'Gravar para todas as obras'}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={
                  gravando || fator === undefined || fator <= 0 || fator === price?.unitFactor
                }
                onClick={() => {
                  if (fator === undefined || fator <= 0) return;
                  void gravar({ unitFactor: fator });
                }}
              >
                Só nesta obra
              </Button>
              {price?.unitFactor !== undefined && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={gravando}
                  onClick={() => void gravar({ unitFactor: null })}
                >
                  Tirar o desta obra
                </Button>
              )}
            </form>
          </section>
        )}

        <section aria-label="Preço digitado" className="flex flex-col gap-2">
          {campo === 'override' ? (
            digitando ? (
              <CorrecaoComMotivo
                rotulo={`Preço digitado de ${material.description}`}
                inicial={price?.overridePrice ?? resolved.unitPrice}
                {...(price?.overrideReason === undefined
                  ? {}
                  : { motivoInicial: price.overrideReason })}
                unidade={`R$${porUnidade}`}
                onAplicar={(valor, motivo) => {
                  void gravar({ override: { price: valor, reason: motivo } }).then((feito) => {
                    if (feito) setDigitando(false);
                  });
                }}
                onCancelar={() => setDigitando(false)}
              />
            ) : (
              <div className="flex flex-col gap-1">
                <p className="text-xs text-(--color-content-muted)">
                  Digitar por cima do médio do compras pede o motivo — seis meses depois, o número
                  sem explicação é um número que alguém apaga.
                </p>
                <div>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={gravando}
                    onClick={() => setDigitando(true)}
                  >
                    {price?.overridePrice === undefined
                      ? 'Digitar outro preço'
                      : 'Trocar o preço digitado'}
                  </Button>
                </div>
              </div>
            )
          ) : (
            <form
              className="flex flex-col gap-1"
              onSubmit={(event) => {
                event.preventDefault();
                if (provisorio === undefined) return;
                void gravar({ provisionalPrice: roundPrice(provisorio) });
              }}
            >
              <p className="text-xs text-(--color-content-muted)">
                Sem médio do compras que valha, vale o provisório — ele não pede motivo.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <NumeroOpcionalInput
                  aria-label={`Preço provisório de ${material.description}`}
                  placeholder="R$"
                  className="h-9 w-32 text-right"
                  value={provisorio}
                  onChange={setProvisorio}
                />
                {porUnidade !== '' && (
                  <span className="text-xs text-(--color-content-muted)">{`R$${porUnidade}`}</span>
                )}
                <Button
                  type="submit"
                  size="sm"
                  variant="secondary"
                  disabled={
                    gravando ||
                    provisorio === undefined ||
                    provisorio < 0 ||
                    provisorio === price?.provisionalPrice
                  }
                >
                  Gravar provisório
                </Button>
              </div>
            </form>
          )}
        </section>
      </div>

      <DialogFooter>
        {price?.overridePrice !== undefined && (
          <Button
            variant="secondary"
            disabled={gravando}
            onClick={() => void gravar({ override: null })}
          >
            Voltar ao médio
          </Button>
        )}
        {price?.provisionalPrice !== undefined && (
          <Button
            variant="secondary"
            disabled={gravando}
            onClick={() => {
              setProvisorio(undefined);
              void gravar({ provisionalPrice: null });
            }}
          >
            Tirar provisório
          </Button>
        )}
        <Button variant="ghost" onClick={onFechar}>
          Fechar
        </Button>
      </DialogFooter>
    </>
  );
}
